import { ActionIcon, Button, Card, Group, Select, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { InputSource, OutputDecl, Slot, Step } from '@/api/analyses';
import type { RunType } from '@/api/runs';
import { ConfigForm, withDefaults, type ConfigValue, type JsonSchema } from '@/features/runs/ConfigForm';
import { brand } from '@/theme/theme';

export interface SequenceEditorProps {
  steps: Step[];
  runTypes: RunType[];
  slots: Slot[];
  onChange: (steps: Step[]) => void;
}

interface RtVersion {
  name: string;
  description: string;
  config_schema: JsonSchema;
}
interface RtInput {
  role: string;
  kind: string;
}

const versionsOf = (rt: RunType | undefined) => (Array.isArray(rt?.versions) ? (rt!.versions as RtVersion[]) : []);
const inputsOf = (rt: RunType | undefined, which: 'required_inputs' | 'optional_inputs') => (Array.isArray(rt?.[which]) ? (rt![which] as RtInput[]) : []);
const outputsOf = (rt: RunType | undefined) => (Array.isArray(rt?.outputs) ? (rt!.outputs as string[]) : []);
const kindOk = (want: string, have: string) => want === '*' || have === '*' || want === have;

export function encodeSource(src: InputSource | undefined): string | null {
  if (!src) return null;
  return 'slot' in src ? `slot:${src.slot}` : `run:${src.run}:${src.kind}`;
}
export function decodeSource(s: string | null): InputSource | undefined {
  if (!s) return undefined;
  if (s.startsWith('slot:')) return { slot: s.slice(5) };
  const [, run, kind] = s.split(':');
  return { run: Number(run), kind };
}

/** Ordered steps of run types with per-role input sources: a slot or an earlier step's output. */
export function SequenceEditor({ steps, runTypes, slots, onChange }: SequenceEditorProps) {
  const { t } = useTranslation('analyses');
  const active = runTypes.filter((r) => r.is_active);
  const set = (i: number, patch: Partial<Step>) => onChange(steps.map((s, n) => (n === i ? { ...s, ...patch } : s)));
  const remove = (i: number) => onChange(steps.filter((_, n) => n !== i));
  const swap = (i: number, j: number) => {
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <Stack gap="md">
      {steps.map((step, i) => {
        const rt = active.find((r) => r.key === step.run_type);
        const versions = versionsOf(rt);
        const chosen = step.version ?? rt?.default_version ?? versions[0]?.name ?? null;
        const schema = (versions.find((v) => v.name === chosen)?.config_schema ?? (rt?.config_schema as JsonSchema | undefined) ?? { type: 'object' }) as JsonSchema;
        const roles = [...inputsOf(rt, 'required_inputs').map((x) => ({ ...x, required: true })), ...inputsOf(rt, 'optional_inputs').map((x) => ({ ...x, required: false }))];
        return (
          <Card key={i} data-testid={`step-${i}`}>
            <Group justify="space-between" mb="sm">
              <Text fw={600}>{t('sequence.step', { n: i + 1 })}</Text>
              <Group gap={4}>
                <ActionIcon variant="subtle" aria-label="up" onClick={() => swap(i, i - 1)}>↑</ActionIcon>
                <ActionIcon variant="subtle" aria-label="down" onClick={() => swap(i, i + 1)}>↓</ActionIcon>
                <Button size="compact-xs" color="red" variant="subtle" onClick={() => remove(i)}>
                  {t('sequence.remove')}
                </Button>
              </Group>
            </Group>
            <Group grow align="flex-start">
              <Select
                label={t('sequence.runType')}
                data={active.map((r) => ({ value: r.key, label: r.key }))}
                value={step.run_type || null}
                onChange={(v) => set(i, { run_type: v ?? '', version: null, config: {}, inputs: {} })}
                allowDeselect={false}
              />
              <Select
                label={t('sequence.version')}
                data={versions.map((v) => ({ value: v.name, label: `${v.name} · ${v.description}` }))}
                value={chosen}
                onChange={(v) => set(i, { version: v, config: {} })}
                disabled={!rt}
                allowDeselect={false}
              />
            </Group>
            {rt && (
              <Stack gap="xs" mt="sm">
                <Text fz={12} fw={700} tt="uppercase" c={brand.textMuted}>
                  {t('sequence.inputs')}
                </Text>
                {roles.map((r) => {
                  const options = [
                    ...slots.filter((s) => kindOk(r.kind, s.kind)).map((s) => ({ value: `slot:${s.role}`, label: t('sequence.fromSlot', { role: s.role, kind: s.kind }) })),
                    ...steps.slice(0, i).flatMap((prev, j) =>
                      outputsOf(active.find((x) => x.key === prev.run_type))
                        .filter((k) => kindOk(r.kind, k))
                        .map((k) => ({ value: `run:${j}:${k}`, label: t('sequence.fromStep', { n: j + 1, kind: k }) })),
                    ),
                  ];
                  return (
                    <Select
                      key={r.role}
                      label={`${r.role} (${r.kind})${r.required ? ' *' : ''}`}
                      data={options}
                      value={encodeSource(step.inputs[r.role])}
                      onChange={(v) => {
                        const inputs = { ...step.inputs };
                        const src = decodeSource(v);
                        if (src) inputs[r.role] = src;
                        else delete inputs[r.role];
                        set(i, { inputs });
                      }}
                      clearable={!r.required}
                      placeholder={t('sequence.unmapped')}
                    />
                  );
                })}
                <Text fz={12} fw={700} tt="uppercase" c={brand.textMuted} mt="xs">
                  {t('sequence.config')}
                </Text>
                <ConfigForm schema={schema} value={step.config as ConfigValue} inherited={withDefaults(schema, {})} onChange={(config) => set(i, { config })} />
              </Stack>
            )}
          </Card>
        );
      })}
      <Button variant="default" onClick={() => onChange([...steps, { run_type: active[0]?.key ?? '', version: null, config: {}, inputs: {} }])}>
        {t('sequence.add')}
      </Button>
    </Stack>
  );
}

export type { OutputDecl };
